/**
 * WASM module binding helpers for ehq-web.
 *
 * Generates and injects Python and JavaScript bindings from module manifests
 * so step authors can `from modules import image_processing` in Python
 * and `modules.imageProcessing.sobel(...)` in JavaScript.
 */

import { WasmModuleService, type ModuleManifest } from './wasm-module.service';

// The binding generator is in the ehq-wasm repo.
// For the build, these are imported as relative paths.
// In production, this would be an npm package.
// For now, use the generated code inline since we can't import from ehq-wasm at build time.

/**
 * Set up WASM module Python bindings in the browser Pyodide instance.
 *
 * Registers `_js_wasm_execute` bridge function and runs generated Python code
 * that creates the `modules` package with typed wrapper classes.
 *
 * @param python The Pyodide instance
 * @param wasmModuleService The WasmModuleService with loaded modules
 */
export async function setupWasmPythonBindings(
  python: any,
  wasmModuleService: WasmModuleService,
): Promise<void> {
  const manifests = wasmModuleService.listModules();
  if (manifests.length === 0) return;

  // Register the bridge function
  python.globals.set(
    '_js_wasm_execute',
    (moduleName: string, funcName: string, argsJson: string): string => {
      return wasmModuleService.executeFunction(moduleName, funcName, argsJson);
    },
  );

  // Generate and run the Python binding code
  const pythonCode = generatePythonBindings(manifests);
  await python.runPythonAsync(pythonCode);

  console.log(
    `[WASM Bindings] Python bindings ready for: ${manifests.map(m => m.name).join(', ')}`,
  );
}

/**
 * Generate JavaScript binding code for WASM modules.
 * Returns a string that can be evaluated to create the `modules` namespace.
 *
 * @param wasmModuleService The WasmModuleService with loaded modules
 * @returns Object with an execute function and the generated modules namespace
 */
export function createWasmJsBindings(
  wasmModuleService: WasmModuleService,
): Record<string, any> | null {
  const manifests = wasmModuleService.listModules();
  if (manifests.length === 0) return null;

  const modules: Record<string, any> = {};

  for (const manifest of manifests) {
    const moduleName = toCamelCase(manifest.name);
    const funcs: Record<string, any> = {};

    for (const func of manifest.functions) {
      const jsFuncName = toCamelCase(func.name);
      funcs[jsFuncName] = (args: Record<string, any>) => {
        // Convert camelCase args to snake_case for the WASM call
        const snakeArgs: Record<string, any> = {};
        for (const input of func.inputs) {
          const camelName = toCamelCase(input.name);
          if (args[camelName] !== undefined) {
            snakeArgs[input.name] = args[camelName];
          } else if (args[input.name] !== undefined) {
            snakeArgs[input.name] = args[input.name];
          } else if (!input.required && input.default !== undefined) {
            snakeArgs[input.name] = input.default;
          }
        }

        const resultJson = wasmModuleService.executeFunction(
          manifest.name,
          func.name,
          JSON.stringify(snakeArgs),
        );
        return JSON.parse(resultJson);
      };
    }

    modules[moduleName] = funcs;
  }

  return modules;
}

// --- Inline binding generator (avoids cross-repo import at build time) ---
// This duplicates the logic from ehq-wasm/tools/binding-generator for the
// browser build. In production, this would be an npm package import.

function toCamelCase(name: string): string {
  return name.replace(/[-_]([a-z])/g, (_, c) => c.toUpperCase());
}

function toPythonIdentifier(name: string): string {
  return name.replaceAll('-', '_');
}

function toPascalCase(name: string): string {
  const camel = toCamelCase(name);
  return camel.charAt(0).toUpperCase() + camel.slice(1);
}

function generatePythonBindings(manifests: ModuleManifest[]): string {
  if (manifests.length === 0) return '';

  const lines: string[] = [];

  lines.push(
    `import sys`,
    `import json`,
    `from types import ModuleType`,
    ``,
    `if 'modules' not in sys.modules:`,
    `    _modules_pkg = ModuleType('modules')`,
    `    _modules_pkg.__package__ = 'modules'`,
    `    _modules_pkg.__path__ = []`,
    `    sys.modules['modules'] = _modules_pkg`,
    `else:`,
    `    _modules_pkg = sys.modules['modules']`,
    ``,
  );

  for (const manifest of manifests) {
    generateModuleClass(lines, manifest);
  }

  return lines.join('\n');
}

/** Generate the Python class and registration lines for a single WASM module. */
function generateModuleClass(lines: string[], manifest: ModuleManifest): void {
  const pyName = toPythonIdentifier(manifest.name);
  const className = `_${toPascalCase(manifest.name)}Module`;

  lines.push(`class ${className}:`);
  if (manifest.description) {
    lines.push(`    """${manifest.description}"""`);
  }
  lines.push(``);

  for (const func of manifest.functions) {
    const params = generateMethodParams(func.inputs);
    lines.push(
      `    @staticmethod`,
      `    def ${func.name}(${params}):`,
    );
    if (func.description) {
      lines.push(`        """${func.description}"""`);
    }
    lines.push(`        _args = {}`);

    for (const input of func.inputs) {
      if (input.required) {
        lines.push(
          `        if ${input.name} is None:`,
          `            raise ValueError("'${input.name}' is required")`,
        );
      }
      lines.push(
        `        if ${input.name} is not None:`,
        `            _args['${input.name}'] = ${input.name}`,
      );
    }

    lines.push(
      `        _result_json = _js_wasm_execute('${manifest.name}', '${func.name}', json.dumps(_args))`,
      `        return json.loads(_result_json)`,
      ``,
    );
  }

  lines.push(
    `_${pyName}_instance = ${className}()`,
    `setattr(_modules_pkg, '${pyName}', _${pyName}_instance)`,
    `sys.modules['modules.${pyName}'] = _${pyName}_instance`,
    ``,
  );
}

function generateMethodParams(inputs: { name: string; required?: boolean; default?: any }[]): string {
  if (inputs.length === 0) return '';
  const params: string[] = ['*'];
  for (const input of inputs) {
    if (input.required) {
      params.push(input.name);
    } else {
      const defaultVal = formatPythonDefault(input.default);
      params.push(`${input.name}=${defaultVal}`);
    }
  }
  return params.join(', ');
}

function formatPythonDefault(value: unknown): string {
  if (value === undefined || value === null) return 'None';
  if (typeof value === 'boolean') return value ? 'True' : 'False';
  if (typeof value === 'string') return `'${value.replaceAll("'", String.raw`\'`)}'`;
  if (typeof value === 'number') return String(value);
  return 'None';
}
